'use client'

import { type ReactNode, useContext, useMemo } from 'react'
import { BookingOverview, resolveEventTherapist } from './BookingEvent'
import { BookingStepLayout } from './BookingStepLayout'
import { BookingContext } from './context'
import { resolveEventLocation, resolveEventOnlineMeetingProvider } from './functions'
import { EventLocation } from './types'

export function BookingEventPaymentShell({ children }: { children: ReactNode }) {
    const {
        bookingEvent,
        categories,
        bookingData,
        therapists,
        therapist: contextTherapist,
        credits,
        useCreditsForBooking,
        overviewSelectedLocation
    } = useContext(BookingContext)

    const category = useMemo(
        () => (bookingEvent ? categories[bookingEvent.categoryId] : undefined),
        [bookingEvent, categories]
    )

    const effectiveLocation = useMemo(() => {
        if (!bookingEvent || !category) {
            return EventLocation.NOT_FIXED
        }
        return resolveEventLocation(bookingEvent, categories)
    }, [bookingEvent, category, categories])

    const effectiveOnlineMeetingProvider = useMemo(() => {
        if (!bookingEvent || !category) {
            return undefined
        }
        return resolveEventOnlineMeetingProvider(bookingEvent, category)
    }, [bookingEvent, category])

    const creditForCategory = useMemo(() => {
        if (!bookingEvent || credits.length === 0) {
            return null
        }
        return credits.find((c) => c.categoryId === bookingEvent.categoryId && c.remaining > 0) ?? null
    }, [bookingEvent, credits])

    const isUsingCredits = !!creditForCategory && useCreditsForBooking

    const therapist = useMemo(
        () =>
            bookingEvent
                ? resolveEventTherapist(bookingEvent.therapist, therapists, contextTherapist)
                : contextTherapist,
        [bookingEvent, therapists, contextTherapist]
    )

    const selectedLocation = bookingData?.selectedLocation ?? overviewSelectedLocation

    if (!bookingEvent || !category) {
        return children
    }

    return (
        <BookingStepLayout
            overview={
                <BookingOverview
                    bookingEvent={bookingEvent}
                    category={category}
                    therapist={therapist}
                    selectedLocation={selectedLocation}
                    effectiveLocation={effectiveLocation}
                    effectiveOnlineMeetingProvider={effectiveOnlineMeetingProvider}
                    isUsingCredits={isUsingCredits}
                />
            }
        >
            {children}
        </BookingStepLayout>
    )
}
